import { DEFAULT_PRICE_COUNTRY, TRANSLATION_CONFIGURATION } from '@hub/libs/constants';
import type { TranslationOption } from '@hub/models/graphql';
import { getRegionLanguage } from './get-price-country.helper';

export const shallowEqual = (a: Record<string, unknown>, b: Record<string, unknown>) => {
  const keys1 = Object.keys(a);
  const keys2 = Object.keys(b);
  if (keys1.length !== keys2.length) {
    return false;
  }
  for (const key of keys1) {
    if (a[key] !== b[key]) {
      return false;
    }
  }
  return true;
};

export function createMarkup(htmlValue: string) {
  return { __html: htmlValue };
}

// can be used to filter undefined|null values by Array.filter and get it typed correctly
export function notEmpty<TValue>(value: TValue | null | undefined): value is TValue {
  return value !== null && value !== undefined;
}

/**
 * Extract a specific localized item from a list in case the graphql  is not able to filter based on locale defined on custom objects json value
 * @param translationOptions[]
 * @param language : string
 */
export function extractLocalized(translationOptions: TranslationOption[] | null, locale: string): any {
  const filterTranslationOptions = (currentLocale: string) =>
    translationOptions?.filter(x => x.locale).find(x => x.locale.replace('_', '-') === currentLocale) ?? null;

  let data = filterTranslationOptions(locale);
  if (!data) {
    data = filterTranslationOptions(TRANSLATION_CONFIGURATION.fallbackLocale);
  }
  if (!data) {
    data = filterTranslationOptions(TRANSLATION_CONFIGURATION.extraFallbackLocale);
  }
  return data;
}

/**
 * Deconstructs a given locale string and defaults region to the DEFAULT_PRICE_COUNTRY to allow for correct delivery time calculation
 * @param locale
 * @returns locale in format 'lang-REGION'
 */
export function getDeliveryTimeLocale(locale: string): string {
  // locale could contain only the language e.g. in /en/chainflex/some-path which we use for the .eu domain
  const { language, region = DEFAULT_PRICE_COUNTRY } = getRegionLanguage(locale);
  return `${language.toLowerCase()}-${region.toUpperCase()}`;
}

export function convertQueryParams(params: Record<string, string | number> | undefined) {
  return params
    ? Object.keys(params).reduce((prev, currentParamName) => {
        const currentParameter = params[currentParamName];
        // We don't want to convert undefined params
        // to a string because it will be 'undefined'
        if (currentParameter === undefined) {
          return prev;
        }
        return `${prev}${prev === '' ? '?' : '&'}${currentParamName}=${encodeURIComponent(currentParameter)}`;
      }, '')
    : '';
}

export function findByLocale<T extends { locale: string }>(withLocales: T[], localeToFind: string) {
  return withLocales.find(withLocale => withLocale.locale.replace('_', '-') === localeToFind);
}

export function convertPriceToCent(price: number): number {
  // if price is -1 or 0 for example (Auf Anfrage), just return 0 without calculating and avoid running into errors in the future
  if (!price || price <= 0) {
    return 0;
  }
  return price * 100;
}

export function isObject(value: unknown) {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function scrollToElement(elementId: string | null, options?: ScrollIntoViewOptions): void {
  const element = document.getElementById(elementId);
  const scrollIntoViewDefaultOption: ScrollIntoViewOptions = { behavior: 'smooth' };
  if (element) {
    element.scrollIntoView({ ...scrollIntoViewDefaultOption, ...options });
  }
}

// converts string to array e.g. "[a, b, c]" => ["a", "b", "c"]
export function stringToArray(input: string): string[] {
  try {
    return JSON.parse(input) as string[];
  } catch {
    // Remove the brackets at the start and the end
    // TODO: temporary fix until json-conform format is applied to data
    return input
      .trim()
      .slice(1, -1)
      .split(',')
      .map(item => item.trim());
  }
}
